
import { supabase } from "@/integrations/supabase/client";
import { Account, FinancialData, KpiData, FrameworkData } from './types';
import { toast } from "sonner";

export const fetchFinancialData = async (companyId: string): Promise<FinancialData | null> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error("User not authenticated");
      return null;
    }
    
    // First try to get data for the current user
    let { data, error } = await supabase
      .from('finance_summary')
      .select('*')
      .eq('company_id', companyId)
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    // If no data found for current user, check subordinates
    if (!data && !error) {
      const { data: subordinateData, error: subordinateError } = await supabase
        .from('finance_summary')
        .select('*')
        .eq('company_id', companyId)
        .in('user_id', await getSubordinateIds(userId))
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
        
      if (subordinateError) {
        console.error("Error fetching subordinate financial data:", subordinateError);
        return null;
      }
      
      data = subordinateData;
    }
    
    if (error) {
      console.error("Error fetching financial data:", error);
      return null;
    }
    
    if (data) {
      // Convert JSON arrays to number arrays
      return {
        labels: Array.isArray(data.fiscal_years) ? data.fiscal_years.map(String) : [],
        revenue: Array.isArray(data.revenue) ? data.revenue.map(Number) : [],
        profit: Array.isArray(data.gross_profit) ? data.gross_profit.map(Number) : [],
        eps: Array.isArray(data.eps) ? data.eps.map(Number) : [],
        cashFlow: Array.isArray(data.free_cash_flow) ? data.free_cash_flow.map(Number) : [],
        debt: Array.isArray(data.total_debt) ? data.total_debt.map(Number) : [],
        ebitdaMargins: Array.isArray(data.ebitda_margins) ? data.ebitda_margins.map(Number) : []
      };
    }
    
    return null;
  } catch (error) {
    console.error("Error fetching financial data:", error);
    return null;
  }
};

export const fetchKpiData = async (companyId: string): Promise<{ data: KpiData | null, updated_at?: string }> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error("User not authenticated");
      return { data: null };
    }
    
    // First try to get data for the current user
    let { data, error } = await supabase
      .from('customer_kpis')
      .select('*, updated_at')
      .eq('company_id', companyId)
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    // If no data found for current user, check subordinates
    if (!data && !error) {
      const { data: subordinateData, error: subordinateError } = await supabase
        .from('customer_kpis')
        .select('*, updated_at')
        .eq('company_id', companyId)
        .in('user_id', await getSubordinateIds(userId))
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();
        
      if (subordinateError) {
        console.error("Error fetching subordinate KPI data:", subordinateError);
        return { data: null };
      }
      
      data = subordinateData;
    }
    
    if (error) {
      console.error("Error fetching KPI data:", error);
      return { data: null };
    }
    
    if (data) {
      return {
        data: {
          executive: Array.isArray(data.exec_kpis) ? data.exec_kpis : [],
          financial: Array.isArray(data.core_financial_kpis) ? data.core_financial_kpis : [],
          initiatives: Array.isArray(data.business_initiatives) ? data.business_initiatives : [],
          operating: Array.isArray(data.operating_kpis) ? data.operating_kpis : [],
          solutions: [] // This field doesn't exist in the database, using empty array
        },
        updated_at: data.updated_at
      };
    }
    
    return { data: null };
  } catch (error) {
    console.error("Error fetching KPI data:", error);
    return { data: null };
  }
};

export const fetchFrameworkData = async (companyId: string): Promise<FrameworkData | null> => {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user?.id;
    
    if (!userId) {
      toast.error("User not authenticated");
      return null;
    }
    
    // First try to get data for the current user
    let { data, error } = await supabase
      .from('business_value_framework')
      .select('*')
      .eq('company_id', companyId)
      .eq('user_id', userId);
    
    // If no data found for current user, check subordinates
    if ((!data || data.length === 0) && !error) {
      const subordinateIds = await getSubordinateIds(userId);
      if (subordinateIds.length > 0) {
        const { data: subordinateData, error: subordinateError } = await supabase
          .from('business_value_framework')
          .select('*')
          .eq('company_id', companyId)
          .in('user_id', subordinateIds);
          
        if (subordinateError) {
          console.error("Error fetching subordinate framework data:", subordinateError);
          return null;
        }
        
        data = subordinateData;
      }
    }
    
    if (error) {
      console.error("Error fetching framework data:", error);
      return null;
    }
    
    if (data && data.length > 0) {
      const nodes = data.map(item => ({
        id: item.id,
        type: item.kpi_type || 'default',
        position: { x: Number(item.position_x) || 0, y: Number(item.position_y) || 0 },
        data: {
          title: item.title,
          description: item.description,
          kpiType: item.kpi_type
        }
      }));
      
      // Extract edges from the JSON data stored in the edges field
      const edges: any[] = [];
      data.forEach(item => {
        if (item.edges) {
          try {
            const parsedEdges = typeof item.edges === 'string' 
              ? JSON.parse(item.edges) 
              : item.edges;
            
            if (Array.isArray(parsedEdges)) {
              parsedEdges.forEach((edge: any) => {
                if (edge.source && edge.target) {
                  edges.push({
                    id: `${edge.source}-${edge.target}`,
                    source: edge.source,
                    target: edge.target
                  });
                }
              });
            }
          } catch (e) {
            console.error("Error parsing edges data:", e);
          }
        }
      });
      
      return { nodes, edges };
    }
    
    return null;
  } catch (error) {
    console.error("Error fetching framework data:", error);
    return null;
  }
};

// Helper function to get subordinate user IDs
const getSubordinateIds = async (supervisorId: string): Promise<string[]> => {
  try {
    const { data, error } = await supabase
      .rpc('get_user_subordinates', { supervisor_uuid: supervisorId });
    
    if (error) {
      console.error("Error fetching subordinates:", error);
      return [];
    }
    
    return data ? data.map((row: any) => row.subordinate_id) : [];
  } catch (error) {
    console.error("Error in getSubordinateIds:", error);
    return [];
  }
};

export const generateInsightsService = async (selectedAccount: Account, financial: FinancialData | null, kpi: KpiData | null, framework: FrameworkData | null) => {
  const { data, error } = await supabase.functions.invoke('analyze-sales-strategy', {
    body: { 
      financialData: financial, 
      kpiData: kpi, 
      frameworkData: framework,
      selectedAccount 
    }
  });
  
  if (error) {
    console.error("Error invoking edge function:", error);
    throw new Error(`Error generating insights: ${error.message}`);
  }
  
  if (data.success && data.analysis) {
    return data.analysis;
  } else {
    throw new Error(data.error || "Unknown error occurred");
  }
};
